import { MarkLabelPosition, HistoryRecord, IDType, OpType, Scene, VideoType, Poster } from './common';
import { TVCoverData } from '@/pages/playDetail/types';

const { LowerLeft, LowerRight, LowerCenter, UpperLeft, UpperRight } = MarkLabelPosition;
export const markLabelPositionToKey = {
  [LowerLeft]: 'bottom-left',
  [LowerRight]: 'bottom-right',
  [LowerCenter]: 'bottom-center',
  [UpperLeft]: 'top-left',
  [UpperRight]: 'top-right',
};

export enum PosterLabelType {
  IMG = 'img',
  TEXT = 'text',
}

export type PosterLabel = {
  type: PosterLabelType;
  content: string;
  position: (typeof markLabelPositionToKey)[keyof typeof markLabelPositionToKey];
};

// 后台返回的
export interface HistoryResItem {
  record: HistoryRecord;
  operation_type: OpType;
  record_type: IDType;
  scene: Scene;
  platform: number;
  country_code: string;
}

// 本地保存的
export interface HistorySaveItem {
  // 核心 ID 字段
  vid: string;
  cid: string;
  pid?: string;
  // 时间字段（驼峰命名）
  modifyTime: string;
  // 播放进度（驼峰命名）
  videoPlayTime: number;
  videoTotalTime: number;

  // 视频类型
  type: VideoType;
}

export interface CoverInfo {
  horizontal: string;
  vertical: string;
}

export interface HistoryShowItem {
  cid?: string;
  vid?: string;
  pid?: string;
  title?: string; // cid
  artTitle?: string;
  vidTitle?: string;
  cover?: CoverInfo; // cid
  fallbackCover?: CoverInfo; // 带标题的cid cover
  vidCover?: CoverInfo;
  cornerLabels?: PosterLabel[];
  updateInfo?: string;
  isTrailer?: boolean;
  progress?: number;
  modifyTime?: string;

  videoPlayTime?: number;
  videoTotalTime?: number;
  type?: VideoType;
  idx?: number;
}

export enum HistoryPageFocusModule {
  LoginBtn = 'loginBtn',
  TodayModule = 'todayModule',
  WeekModule = 'weekModule',
  EarlyModule = 'earlyModule',
  DeleteModal = 'deleteModal',
  HistoryPage = 'historyPage',
}

export enum MoveDirection {
  Up = 'up',
  Down = 'down',
  UnKnown = 'unKnown',
}

// 配合上方HistoryPageFocusModule使用
export enum HistoryModuleType {
  TodayModule = HistoryPageFocusModule.TodayModule,
  WeekModule = HistoryPageFocusModule.WeekModule,
  EarlyModule = HistoryPageFocusModule.EarlyModule,
}

export const focusKeyToNextKey = {
  [HistoryPageFocusModule.LoginBtn]: HistoryPageFocusModule.TodayModule,
  [HistoryPageFocusModule.TodayModule]: HistoryPageFocusModule.WeekModule,
  [HistoryPageFocusModule.WeekModule]: HistoryPageFocusModule.EarlyModule,
  [HistoryPageFocusModule.EarlyModule]: HistoryPageFocusModule.LoginBtn,
};

export const focusKeyToPrevKey = {
  [HistoryPageFocusModule.LoginBtn]: HistoryPageFocusModule.LoginBtn,
  [HistoryPageFocusModule.TodayModule]: HistoryPageFocusModule.LoginBtn,
  [HistoryPageFocusModule.WeekModule]: HistoryPageFocusModule.TodayModule,
  [HistoryPageFocusModule.EarlyModule]: HistoryPageFocusModule.WeekModule,
};

export type HistoryUiData = Poster & HistoryRecord;

export interface HistoryModule {
  moduleTitle: string;
  moduleIdx: number;
  moduleType: HistoryModuleType;
  historyList: HistoryShowItem[];
}

export interface LocalHistoryInfo {
  history: HistorySaveItem[];
  dataVersion?: string;
  vuid?: string;
}

export interface HistoryUpdateParams {
  currentHistory: HistorySaveItem[];
  historyItem: HistorySaveItem;
  operationType: OpType;
  vuid: string;
}

export interface HistoryUpdateCurShowParams {
  currentHistory: HistoryShowItem[];
  historyItem: HistoryShowItem;
  operationType: OpType;
}

export interface NextPageInfo {
  dataVersion: string;
  pageContext: string;
  hasNextPage: boolean;
}

export interface HistoryFetchAllHistoryParams extends NextPageInfo {
  historyInfo: HistoryResItem[];
  startVuid: string; // 用于识别当前用户
}

export interface HistoryFetchParams {
  historyInfo: HistorySaveItem[];
  dataVersion: string;
}

export interface HistoryApiResponse {
  data_version: string;
  page_context: string;
  has_next_page: boolean;
  updated: HistoryResItem[];
}

export interface HistoryStoreState {
  vuid: string;
  curHistory: HistorySaveItem[];
  curShowHistory: HistoryShowItem[];

  isLoading: boolean;
  isLoaded: boolean;
}

export interface HistoryStoreActions {
  setVuid: (vuid: string) => void;

  initHistory: () => Promise<void>;
  fetchAllHistory: (fetchAllHistoryParams: HistoryFetchAllHistoryParams) => Promise<{
    serverHistory: HistoryResItem[];
    dataVersion: string;
  }>;
  fetchHistory: (fetchHistoryParams: HistoryFetchParams) => Promise<void>;
  fetchHistoryUiData: (historyList: HistorySaveItem[]) => Promise<HistoryUiData[]>;
  fetchHistoryCoverData: (allHistory: HistorySaveItem[]) => Promise<TVCoverData[]>;
  syncLocalHistoryIfNeeded: (localHistory: HistorySaveItem[], serverHistory: HistorySaveItem[]) => Promise<void>;
  queryHistoryByCid: (cid: string) => HistorySaveItem | null;
  deleteAllHistory: () => Promise<boolean>;
  updateHistory: (historyItem: HistorySaveItem, operationType: OpType) => Promise<boolean>;
  syncServerTime: () => Promise<string | null>;

  reset: () => void;
}

export type HistoryStoreStore = HistoryStoreState & HistoryStoreActions;

export interface HistoryPageState {
  // 选中的模块
  selectModule: HistoryPageFocusModule;

  // 打开删除弹窗时选中的海报信息，用于删除单个海报记录时使用
  curSelectedPosterInfo: HistoryShowItem | null;
  curSelectedPosterModule: HistoryPageFocusModule;

  // history 模块数据
  historyModule: HistoryModule[];

  // 只限于在 loginbtn 以及三个历史记录module间
  // 历史记录删除弹窗关闭时
  moveDirection: MoveDirection;

  isInited: boolean;
  isDeleting: boolean;
  isDeleted: boolean;
}

export interface HistoryPageActions {
  init: (isInit: boolean) => void;

  deleteHistory: (historyItem: HistoryShowItem) => Promise<boolean>;
  deleteAllHistory: () => Promise<boolean>;
  keyToVal: <K extends keyof HistoryPageStore>(key: K) => HistoryPageStore[K];
  setKeyVal: <K extends keyof HistoryPageStore>(key: K, val: HistoryPageStore[K]) => void;

  reset: () => void;
}

export type HistoryPageStore = HistoryPageState & HistoryPageActions;
